import { Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import cn from 'classnames'
import { isEqual } from 'lodash'
import moment from 'moment'
import React from 'react'
import { Mutation } from 'react-apollo'
import { i18n } from '../../index'
import { HesburgerEfficiencyBoundary } from '../../station/stationTypes'
import RequiresAction from '../RequiresAction'
import { saveHesburgerEfficiencyFactor } from '../weeklyInfoQueries'
import css from './hesburgerEfficiencyFactorTable.css'

type HesburgerEfficiencyFactor = {
  ipShare: number | string
  avgPurchase: number | string
  salesMargin: number | string
  productWastage: number | string
  raWastage: number | string
  inventoryDifferences: number | string
  intakes: number | string
  readinessCheeseBurger: number | string
  readinessDoubleBurger: number | string
  readinessCampaign: number | string
  isActionRequired: boolean | null
}

type HesburgerEfficiencyFactorTableProps = {
  efficiencyBoundary: HesburgerEfficiencyBoundary | null
  efficiencyFactors: HesburgerEfficiencyFactor | null
  stationId: string
  date: string
  refetch: (...args: Array<any>) => any
}

type HesburgerEfficiencyFactorTableState = {
  editing: boolean
  efficiencyFactors: HesburgerEfficiencyFactor
}

const defaultEfficiencyFactors: HesburgerEfficiencyFactor = {
  ipShare: '',
  avgPurchase: '',
  salesMargin: '',
  productWastage: '',
  raWastage: '',
  inventoryDifferences: '',
  intakes: '',
  readinessCheeseBurger: '',
  readinessDoubleBurger: '',
  readinessCampaign: '',
  isActionRequired: null,
}

const fields: string[] = [
  'ipShare',
  'avgPurchase',
  'readinessDoubleBurger',
  'readinessCheeseBurger',
  'readinessCampaign',
  'salesMargin',
  'inventoryDifferences',
  'raWastage',
  'productWastage',
  'intakes',
]

const aboveIsGood: string[] = [
  'ipShare',
  'avgPurchase',
  'salesMargin',
  'readinessCheeseBurger',
  'readinessDoubleBurger',
  'readinessCampaign',
  'intakes',
  'productWastage',
  'raWastage',
]

const belowIsGood: string[] = ['productWastage', 'inventoryDifferences', 'intakes']

class HesburgerEfficiencyFactorTable extends React.Component<
  HesburgerEfficiencyFactorTableProps,
  HesburgerEfficiencyFactorTableState
> {
  constructor(props) {
    super(props)
    const efficiencyFactors = props.efficiencyFactors ? props.efficiencyFactors : defaultEfficiencyFactors
    this.state = {
      editing: false,
      efficiencyFactors: { ...efficiencyFactors },
    }
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.efficiencyFactors, this.props.efficiencyFactors)) {
      this.setState({
        efficiencyFactors: { ...this.props.efficiencyFactors } as HesburgerEfficiencyFactor,
      })
    }
  }

  onChange = (key: string, value: string) => {
    const { efficiencyFactors } = this.state
    const newData = { ...efficiencyFactors, [key]: value }
    this.setState({
      efficiencyFactors: { ...newData },
    })
  }
  onSave = async (onMutate: (...args: Array<any>) => any) => {
    const { stationId, date } = this.props
    const { efficiencyFactors } = this.state
    const notNullData: Partial<HesburgerEfficiencyFactor> = {}
    Object.keys(efficiencyFactors).forEach((key) => {
      if (fields.includes(key)) {
        notNullData[key] = !isNaN(parseFloat(efficiencyFactors[key])) ? parseFloat(efficiencyFactors[key]) : 0
      }
    })
    notNullData.isActionRequired = efficiencyFactors.isActionRequired
    const variables = {
      stationId,
      date,
      data: notNullData,
    }
    await onMutate({
      variables,
    })
    this.setState({
      editing: false,
    })
    this.props.refetch()
  }
  getIsPositiveOrNegative = (field: string) => {
    const { efficiencyFactors } = this.state
    const { efficiencyBoundary } = this.props

    if (!efficiencyBoundary) {
      return null
    }

    const value = efficiencyFactors[field]
    const boundary = efficiencyBoundary[field]

    if (aboveIsGood.includes(field)) {
      return value >= boundary ? css.positiveCell : css.negativeCell
    } else if (belowIsGood.includes(field)) {
      return value <= boundary ? css.positiveCell : css.negativeCell
    }

    return null
  }
  onActionRequiredChange = (onMutate: (...args: Array<any>) => any) => {
    this.setState(
      (prevState) => ({
        efficiencyFactors: {
          ...prevState.efficiencyFactors,
          isActionRequired: !prevState.efficiencyFactors.isActionRequired,
        },
      }),
      () => this.onSave(onMutate),
    )
  }

  render() {
    const { editing, efficiencyFactors } = this.state
    const { stationId, date } = this.props
    return (
      <div className={css.content}>
        <Typography variant={'h6'} gutterBottom>
          {i18n.t('weeklyInfo.title.hesburgerEfficiency', {
            month: moment(date).format('MMMM'),
          })}
        </Typography>
        <Paper>
          <Table size='small'>
            <TableHead>
              <TableRow>
                {fields.map((field) => (
                  <TableCell key={field} className={cn(css.tableCell, css.cellCenter)}>
                    {i18n.t(`admin.stations.title.hesburgerTable.${field}`)}
                  </TableCell>
                ))}
                <TableCell className={cn(css.tableCell, css.cellCenter)}>
                  {i18n.t('weeklyInfo.heading.isActionRequired')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {fields.map((field) => {
                  return editing ? (
                    <TableCell key={field} className={css.tableCell}>
                      <div className={css.flexCell}>
                        <TextField
                          value={efficiencyFactors[field]}
                          type='number'
                          onChange={(e) => this.onChange(field, e.target.value)}
                        />
                      </div>
                    </TableCell>
                  ) : (
                    <TableCell
                      key={field}
                      className={cn(css.tableCell, css.cellCenter, this.getIsPositiveOrNegative(field))}
                    >
                      {typeof efficiencyFactors[field] === 'number' ? efficiencyFactors[field].toFixed(2) : ''}
                    </TableCell>
                  )
                })}
                <TableCell className={cn(css.actionCell, css.cellCenter)}>
                  {this.props.efficiencyFactors && (
                    <Mutation mutation={saveHesburgerEfficiencyFactor}>
                      {(onMutate) => (
                        <RequiresAction
                          stationId={stationId}
                          date={date}
                          subject='Hesburger'
                          isActionRequired={efficiencyFactors.isActionRequired}
                          callback={() => this.onActionRequiredChange(onMutate)}
                        />
                      )}
                    </Mutation>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </div>
    )
  }
}

export default HesburgerEfficiencyFactorTable
